import { Head } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'

export default function ReactDocumentation() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Head title="React Component - Documentation" />
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">React Component</h1>
        <p className="text-lg text-gray-600 mb-4">
          Official React component for Simple File Upload
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Getting Started:</strong> Install the React component with <code className="bg-blue-100 px-1 py-0.5 rounded text-blue-800">npm install simple-file-upload-react</code>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Installation & Setup</CardTitle>
          <CardDescription>
            Get started with the Simple File Upload React component
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">1. Install the package</h3>
            <div className="relative">
              <SyntaxHighlighter
                language="bash"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.9rem',
                  borderRadius: '6px'
                }}
              >
                {`npm install simple-file-upload-react`}
              </SyntaxHighlighter>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 text-white hover:bg-gray-700"
                onClick={() => copyToClipboard('npm install simple-file-upload-react')}
              >
                Copy
              </Button>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">2. Import the component</h3>
            <SyntaxHighlighter
              language="javascript"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.9rem',
                borderRadius: '6px'
              }}
            >
              {`import { SimpleFileUpload } from 'simple-file-upload-react'`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">3. Basic usage</h3>
            <SyntaxHighlighter
              language="javascript"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.9rem',
                borderRadius: '6px'
              }}
            >
              {`function MyComponent() {
  return (
    <SimpleFileUpload
      publicKey="YOUR_PUBLIC_KEY"
      onChange={(event) => console.log("Files changed:", event)}
    />
  )
}`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Component Props</CardTitle>
          <CardDescription>
            Configure the uploader behavior with these props
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Prop</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Default</th>
                  <th className="text-left p-2">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">publicKey</code></td>
                  <td className="p-2"><Badge variant="outline">string</Badge></td>
                  <td className="p-2"><Badge variant="destructive">required</Badge></td>
                  <td className="p-2">Your Simple File Upload public API key</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">tag</code></td>
                  <td className="p-2"><Badge variant="outline">string</Badge></td>
                  <td className="p-2">undefined</td>
                  <td className="p-2">Optional tag to categorize uploads</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">multiple</code></td>
                  <td className="p-2"><Badge variant="outline">boolean</Badge></td>
                  <td className="p-2">true</td>
                  <td className="p-2">Whether to allow multiple file uploads</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">maxFileSize</code></td>
                  <td className="p-2"><Badge variant="outline">number</Badge></td>
                  <td className="p-2">5242880 (5MB)</td>
                  <td className="p-2">Maximum file size in bytes</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">maxFiles</code></td>
                  <td className="p-2"><Badge variant="outline">number</Badge></td>
                  <td className="p-2">5</td>
                  <td className="p-2">Maximum number of files allowed</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">accept</code></td>
                  <td className="p-2"><Badge variant="outline">string</Badge></td>
                  <td className="p-2">All file types</td>
                  <td className="p-2">Accepted file types (e.g., "image/*,application/pdf")</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">onChange</code></td>
                  <td className="p-2"><Badge variant="outline">function</Badge></td>
                  <td className="p-2">-</td>
                  <td className="p-2">Callback when files change (added, removed, or upload complete)</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">onUploadDone</code></td>
                  <td className="p-2"><Badge variant="outline">function</Badge></td>
                  <td className="p-2">-</td>
                  <td className="p-2">Callback when all uploads are complete</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2"><code className="text-sm bg-gray-100 px-1 py-0.5 rounded">onReset</code></td>
                  <td className="p-2"><Badge variant="outline">function</Badge></td>
                  <td className="p-2">-</td>
                  <td className="p-2">Callback when component is reset</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Handling File Uploads</CardTitle>
          <CardDescription>
            Access uploaded file information through event callbacks
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Getting uploaded files</h3>
            <p className="text-gray-600 mb-3">
              The <code>onChange</code> callback receives an event object containing file information:
            </p>
            <SyntaxHighlighter
              language="javascript"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.9rem',
                borderRadius: '6px'
              }}
            >
              {`function MyComponent() {
  const [uploadedFiles, setUploadedFiles] = useState([])

  const handleFileChange = (event) => {
    console.log('Event:', event)

    // Access files from event.allFiles
    if (event.allFiles) {
      const files = event.allFiles.map(file => ({
        id: file.id,
        name: file.name,
        url: file.cdnUrl,
        size: file.size
      }))
      setUploadedFiles(files)
    }
  }

  return (
    <SimpleFileUpload
      publicKey="YOUR_PUBLIC_KEY"
      onChange={handleFileChange}
    />
  )
}`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Resetting the Component</CardTitle>
          <CardDescription>
            Programmatically clear all uploads using refs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-gray-600 mb-3">
              Use a ref to access the component's reset method:
            </p>
            <SyntaxHighlighter
              language="javascript"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.9rem',
                borderRadius: '6px'
              }}
            >
              {`function MyComponent() {
  const fileUploadRef = useRef(null)
  const [uploadedFiles, setUploadedFiles] = useState([])

  const handleReset = () => {
    // Clear your local state
    setUploadedFiles([])

    // Reset the component
    if (fileUploadRef.current?.reset) {
      fileUploadRef.current.reset()
    }
  }

  return (
    <>
      <SimpleFileUpload
        ref={fileUploadRef}
        publicKey="YOUR_PUBLIC_KEY"
        onChange={handleFileChange}
        onReset={() => {
          // This fires when component resets itself
          setUploadedFiles([])
        }}
      />

      <button onClick={handleReset}>
        Clear all uploads
      </button>
    </>
  )
}`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Complete Example</CardTitle>
          <CardDescription>
            A full implementation with all features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SyntaxHighlighter
            language="javascript"
            style={atomOneDark}
            customStyle={{
              background: '#21252b',
              padding: '1rem',
              margin: 0,
              fontSize: '.9rem',
              borderRadius: '6px'
            }}
          >
            {`import { useState, useRef } from 'react'
import { SimpleFileUpload } from 'simple-file-upload-react'

function FileUploadDemo() {
  const [uploadedFiles, setUploadedFiles] = useState([])
  const fileUploadRef = useRef(null)

  const handleFileChange = (event) => {
    if (event.allFiles) {
      const files = event.allFiles.map(file => ({
        id: file.id || file.name,
        name: file.name,
        url: file.cdnUrl || file.preview,
        size: file.size
      }))
      setUploadedFiles(files)
    }
  }

  const handleReset = () => {
    setUploadedFiles([])
    if (fileUploadRef.current?.reset) {
      fileUploadRef.current.reset()
    }
  }

  return (
    <div>
      <SimpleFileUpload
        ref={fileUploadRef}
        publicKey="YOUR_PUBLIC_KEY"
        onChange={handleFileChange}
        onReset={() => setUploadedFiles([])}
        multiple={true}
        maxFiles={5}
        maxFileSize={10485760} // 10MB
        accept="image/*,application/pdf"
      />

      {/* Display uploaded files */}
      <div className="uploaded-files">
        {uploadedFiles.map(file => (
          <div key={file.id}>
            <img src={file.url} alt={file.name} />
            <p>{file.name}</p>
          </div>
        ))}
      </div>

      {uploadedFiles.length > 0 && (
        <button onClick={handleReset}>Clear all</button>
      )}
    </div>
  )
}`}
          </SyntaxHighlighter>
        </CardContent>
      </Card>
    </div>
  )
}